import {
  useCallback,
  useEffect,
  useState,
  FC,
  useMemo,
  SetStateAction,
  Dispatch
} from "react"
import { HeadFC, graphql, PageProps } from "gatsby"
import { useDispatch, useSelector } from "react-redux"
import classNames from "classnames"
import Lottie from "react-lottie"
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer"
import Joyride, { CallBackProps, STATUS } from "react-joyride"

// components
import ActionComplex, {
  ActionComplexMicrocopy,
  ActionComplexProSubMicrocopy,
  LiveSupportChannelMicrocopy,
  LiveSupportUnavailableMicrocopy
} from "../../components/ActionComplex/ActionComplex"
import { HeadUniversal } from "../../components/HeadUniversal/HeadUniversal"
import { Layout } from "../../components/Layout/Layout"
import LiveChat from "../../components/ChatWidget/LiveChat"
import SupportWidget from "../../components/SupportWidget/SupportWidget"
import { InlineAlert } from "../../components/Marmalade/InlineAlert/InlineAlert"
import { LogInLegacy } from "../../components/LogIn/LogInLegacy"
import {
  ContactForm,
  MicroCopy,
  Options
} from "../../components/ContactForm/ContactForm"
import { PersonalizationSection } from "../../components/PersonalizationSection/PersonalizationSection"
import SupportOptions from "../../components/SupportOptions/SupportOptions"
import { setInlineAlertProps } from "../../store/slices/alert.slice"
import { BackCrumb } from "../../components/Breadcrumbs/BackCrumb"
import { HrefLangTags } from "../../components/HrefLangTags/HrefLangTags"
import { CanonicalTag } from "../../components/CanonicalTag/CanonicalTag"

// icons
import CommonQuestionsSection from "../../components/CommonQuestionsSection/CommonQuestionsSection"
import {
  ChatIcon,
  JoyrideTourGuide,
  JOYRIDE_TOURGUIDE_FLOATER_STYLES_DEFAULT,
  MailIcon,
  MegaphoneIcon,
  PhoneIcon,
  Typography
} from "@eventbrite/marmalade"

// data
import { staticHrefLangTags } from "../../utils/seo/href_lang_tag_data"

// lottie data
import * as greenLoaderLottieData from "../../icons/lottie/GreenSpinner/Progress loading_circular_Green.json"

// utils
import { useStatsigClient } from "@statsig/react-bindings"
import { goToGrooveCalender } from "../../utils/supportWidgets/supportUtils"
import {
  generateDescriptionMetaTag,
  generateRobotsMetaTag,
  getTitleText
} from "../../utils/seo/seo_utils"
import { openChat } from "../../components/ChatWidget/LiveChatUtils"
import { events } from "../../utils/heap/tracking_events"
import {
  customRendererToRemovePTag,
  renderRichText
} from "../../utils/contentful/render_rich_text"
import {
  assignLangCode,
  getTheDay,
  isDisabled,
  isLiveSupportedLocale,
  navigateWithPrefix,
  withinBusinessHours
} from "../../utils/general_utils"
import {
  UserProfile,
  userProfileGuardCheck,
  setUserProfile
} from "../../store/slices/user_profile.slice"
import { setMetaData } from "../../store/slices/metadata.slice"
import {
  enrichUserData,
  retrieveOrganization
} from "../../utils/auth/enrich_user_data"

// types
import { RootState } from "../../store/store"
import { Props as LayoutProps } from "../../components/Layout/Layout"
import { QueryParameterKeys } from "../../types/query_parameter.enum"
import { SessionStorageKeys } from "../../types/window_session_storage.enum"
import { SupportChannelAPIKeys } from "../../types/support_channels.enum"

// styles
import * as styles from "./contact-us.module.scss"
import { getFlag } from "../../utils/query_param_flags"
import * as tourGuideStyles from "@eventbrite/marmalade/lib/components/TourGuide/TourGuide.css"

export type PickedLayoutProps = Pick<
  LayoutProps,
  "microCopy" | "footerCopy" | "footerNavCopy"
>

type SupportList =
  Queries.CommonIssuesAndQuestionsQuery["supportLists"]["edges"][0]["node"]
type SupportOffice =
  Queries.CommonIssuesAndQuestionsQuery["supportLists"]["edges"][0]["node"]["phoneSupportOfficeCity"]

type AssortmentPermissions = {
  assortmentName: string
  chatPermission: boolean
  emailPermission: boolean
  phonePermission: boolean
  consultation: boolean
  temporarilyDisabled: string[]
}

type DisabledChannels = {
  chat: boolean
  phone: boolean
  email: boolean
}

export type PageContext = {
  id: string
  node_locale: string
}

const ContactUs: FC<PageProps<Queries.CommonIssuesAndQuestionsQuery>> = (
  pageProps
) => {
  const data = pageProps?.data
  const params = pageProps?.params
  const urlParamFlag = getFlag()
  const [onboard] = useState<boolean>(true)
  const [showCallbackForm, setShowCallbackform] = useState<boolean>(false)
  const [caseId, setCaseId] = useState<string>()
  const [callbackFormSubmitted, setCallbackFormSubmitted] =
    useState<boolean>(false)
  const [isTabletOrLarger, setIsTabletOrLarger] = useState<boolean>(false)
  const [currentOrg, setCurrentOrg] = useState<Options>()
  const [selectedOrgId, setSelectedOrgId] = useState<string>("")
  const [isTogglingOrg, setIsTogglingOrg] = useState<boolean>(false)

  const userProfile = userProfileGuardCheck(
    useSelector((state: RootState) => state.userProfile)
  )
  const isLoggedIn = useSelector((state: RootState) => state?.userProfile)
    ? true
    : false
  const currentLangCode = useSelector(
    (state: RootState) => state.metaData.langCode
  )
  const hasAttemptedLogin = useSelector(
    (state: RootState) => state?.metaData?.hasAttemptedLogin
  )
  const showLiveChatGlobal = useSelector(
    (state: RootState) => state?.metaData?.showLiveChatGlobal
  )
  const isEn = params?.node_locale.includes("en")
  const isPaid = userProfile?.isPaidCreator

  const dispatch = useDispatch()

  const userId = userProfile?.userData?.id
  const { client } = useStatsigClient()
  const statsigUserId = client?.getContext()?.user?.userID
  const chatbotPlacementExp =
    userId && client && statsigUserId === userId
      ? client.getExperiment("help_center_chatbot_placement_v2")
      : null
  const showChatbotFirst =
    userId && chatbotPlacementExp
      ? chatbotPlacementExp?.get("showChatbotFirst", false)
      : false

  if (process.env.GATSBY_DEBUG === "true") {
    if (!userId || !client) {
      console.log("[Statsig] experiment evaluation skipped", {
        experiment: "help_center_chatbot_placement_v2",
        reason: !userId ? "no userId" : "no client",
        hasUserId: Boolean(userId),
        hasClient: Boolean(client)
      })
    } else if (chatbotPlacementExp) {
      console.log("[Statsig] experiment result", {
        experiment: "help_center_chatbot_placement_v2",
        groupName: chatbotPlacementExp.groupName,
        showChatbotFirst,
        userId
      })
    }
  }

  // Store experiment status in Redux metadata for Decagon
  useEffect(() => {
    if (userId && chatbotPlacementExp && showChatbotFirst) {
      dispatch(
        setMetaData({
          chatbotFirstExperiment: true
        })
      )
      if (process.env.GATSBY_DEBUG === "true") {
        console.log("[Statsig] experiment metadata stored", {
          experiment: "help_center_chatbot_placement_v2",
          groupName: chatbotPlacementExp.groupName,
          metadataKey: "chatbot_first_experiment",
          value: true
        })
      }
    }
  }, [userId, chatbotPlacementExp?.groupName, showChatbotFirst, dispatch])

  useEffect(() => {
    assignLangCode(currentLangCode, dispatch)
  }, [])

  // Organization switching effects
  useEffect(() => {
    ;(async () => {
      if (selectedOrgId && userProfile?.allData?.userAuthData) {
        setIsTogglingOrg(true)
        const enrichedUserData = await enrichUserData(
          userProfile.allData.userAuthData,
          selectedOrgId
        )

        if (enrichedUserData) {
          dispatch(setUserProfile(enrichedUserData))
        }
        setIsTogglingOrg(false)
      }
    })()
  }, [selectedOrgId, userProfile?.allData?.userAuthData, dispatch])

  useEffect(() => {
    const selectedOrg = retrieveOrganization(
      userProfile?.allData?.orgData,
      userProfile?.organizationId
    )
    setCurrentOrg({
      label: selectedOrg?.name ?? "",
      value: selectedOrg?.id ?? ""
    })
  }, [userProfile?.allData?.orgData, userProfile?.organizationId])

  // Handle responsive layout for tablets
  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined") {
        // Consider tablets as 768px and above for horizontal layout
        setIsTabletOrLarger(window.innerWidth >= 768)
      }
    }

    // Set initial value
    handleResize()

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize)
      return () => window.removeEventListener("resize", handleResize)
    }
  }, [])

  const isLiveSupportLocale = useMemo<boolean>(() => {
    return isLiveSupportedLocale(
      data?.liveSupportProvisionedRegions?.supportedLocales as string[],
      currentLangCode
    )
  }, [data?.liveSupportProvisionedRegions?.supportedLocales, currentLangCode])

  const withinChatHours = useMemo<boolean>(() => {
    if (isLoggedIn) {
      const supportList: SupportList | undefined =
        data?.supportLists?.edges?.find(
          (edge) =>
            edge?.node?.localeCode?.toLowerCase() ===
            currentLangCode.toLowerCase()
        )?.node
      let supportOffice: SupportOffice
      if (
        userProfile?.isHighValue ||
        userProfile?.assortmentPlan === "package3"
      ) {
        supportOffice = supportList?.premiumSupportOfficeCity as SupportOffice
      } else {
        supportOffice = supportList?.supportOfficeCity as SupportOffice
      }
      const today = getTheDay(supportOffice?.businessHoursTimeZone as string)
      if (supportOffice) {
        return withinBusinessHours(
          supportOffice?.businessHoursTimeZone as string,
          supportOffice?.[today as keyof typeof supportOffice] as string
        )
      }
    }
    return false
  }, [data?.supportLists, userProfile])

  const withinPhoneHours = useMemo<boolean>(() => {
    if (isLoggedIn) {
      const supportList: SupportList | undefined =
        data?.supportLists?.edges?.find(
          (edge) =>
            edge?.node?.localeCode?.toLowerCase() ===
            currentLangCode.toLowerCase()
        )?.node
      const supportOffice: SupportOffice =
        supportList?.phoneSupportOfficeCity as SupportOffice

      const today = getTheDay(supportOffice?.businessHoursTimeZone as string)
      if (supportOffice) {
        return withinBusinessHours(
          supportOffice?.businessHoursTimeZone as string,
          supportOffice?.[today as keyof typeof supportOffice] as string
        )
      }
    }
    return false
  }, [data?.supportLists, userProfile])

  const userAssortmentPermissions =
    useMemo<AssortmentPermissions | null>(() => {
      type UserType =
        | "Gold"
        | "Silver"
        | "Bronze"
        | "Free"
        | "Attendee"
        | "SalesCreator"

      if (!isLiveSupportLocale || !userProfile) {
        return null
      }

      if (isLoggedIn && hasAttemptedLogin && userProfile !== null) {
        // figure out what the user's UserType is
        let userType: UserType = "Attendee"

        if (userProfile?.creatorTier === "Gold") {
          userType = "Gold"
        } else if (userProfile?.creatorTier === "Silver") {
          userType = "Silver"
        } else if (userProfile?.creatorTier === "Bronze") {
          userType = "Bronze"
        } else if (
          userProfile?.creatorTier === "Free" &&
          (userProfile?.allData?.userEventsData?.events?.length ?? 0) > 0
        ) {
          userType = "Free"
        } else if (userProfile?.isSalesCreator) {
          userType = "SalesCreator"
        } else {
          userType = "Attendee"
        }

        // find the user's matching assortmentPermission
        const assortmentPermission =
          data?.assortmentPermissions?.assortments?.find((assortment) => {
            return assortment?.assortmentName === userType
          }) as AssortmentPermissions

        return assortmentPermission
      }
      return null
    }, [
      data?.assortmentPermissions?.assortments,
      userProfile,
      isLiveSupportLocale
    ])

  const multipleContactChannels = useMemo<boolean>(() => {
    let contactChannelsAvailable = 0

    for (const key in userAssortmentPermissions) {
      if (
        userAssortmentPermissions[key as keyof AssortmentPermissions] === true
      ) {
        contactChannelsAvailable++
      }
    }

    return contactChannelsAvailable > 1
  }, [userAssortmentPermissions])

  // Function that returns a boolean, true if all of the users true assortment permissions have a corresponding entry in the temporarily disabled array
  const allLiveSupportIsTemporarilyDisabled = useMemo<boolean>(() => {
    if (!userAssortmentPermissions) {
      return false
    }
    if (userAssortmentPermissions?.assortmentName === "Attendee") {
      return false
    }
    for (const permission in userAssortmentPermissions) {
      // Skip over the assortmentName property
      if (
        typeof userAssortmentPermissions[
          permission as keyof AssortmentPermissions
        ] === "string"
      ) {
        continue
      }
      if (
        userAssortmentPermissions?.[
          permission as keyof AssortmentPermissions
        ] === true &&
        !userAssortmentPermissions?.temporarilyDisabled?.includes(permission)
      ) {
        return false
      }
    }
    return true
  }, [userAssortmentPermissions])

  useEffect(() => {
    if (userProfile) {
      events?.userProfileData("User Profile", userProfile as UserProfile)
    }
  }, [userProfile])

  const callbackClickHandler = useCallback(() => {
    if (
      withinPhoneHours &&
      (userAssortmentPermissions?.phonePermission === true ||
        userProfile?.isSalesCreator)
    ) {
      navigateWithPrefix(
        `/contact-us/?form=${data?.requestCallBackProps?.contactFormUrl}`
      )
    }
  }, [
    data?.requestCallBackProps?.contactFormUrl,
    userAssortmentPermissions?.phonePermission,
    userProfile?.isSalesCreator,
    withinPhoneHours
  ])

  const toggleCallbackForm = useCallback(
    (removeQueryParam = false) => {
      const willCloseForm = showCallbackForm === true

      setShowCallbackform(!showCallbackForm)

      if (typeof window !== "undefined") {
        window?.scroll(0, 0)

        // Remove form query parameter when closing the form (either after successful submission or when user clicks back)
        if (willCloseForm || removeQueryParam) {
          const queryParams = new URLSearchParams(window.location.search)
          queryParams.delete("form")
          const newUrl = queryParams.toString()
            ? `${window.location.pathname}?${queryParams.toString()}`
            : window.location.pathname
          window.history.replaceState({}, "", newUrl)
        }
      }
    },
    [showCallbackForm]
  )

  // Handle email form success (when caseId is set)
  useEffect(() => {
    if (caseId) {
      const alertData = data?.successAlertEmail

      dispatch(
        setInlineAlertProps({
          active: true,
          title: alertData?.alertHeader || "Message received",
          description: alertData?.alertDescription?.raw
            ? `${documentToPlainTextString(
                JSON.parse(alertData.alertDescription.raw)
              )} ${caseId}`
            : `Your message has been submitted successfully. Case number: ${caseId}`,
          variant: "success"
        })
      )
    }
  }, [caseId, data?.successAlertEmail, dispatch])

  // Handle callback form success
  useEffect(() => {
    if (callbackFormSubmitted) {
      const alertData = data?.successAlertCallback

      dispatch(
        setInlineAlertProps({
          active: true,
          title: alertData?.alertHeader || "Message received",
          description: alertData?.alertDescription?.raw
            ? documentToPlainTextString(
                JSON.parse(alertData.alertDescription.raw)
              )
            : "A support representative will contact you shortly.",
          variant: "success"
        })
      )

      setCallbackFormSubmitted(false)
    }
  }, [
    callbackFormSubmitted,
    data?.successAlertCallback,
    dispatch,
    params?.node_locale,
    setCallbackFormSubmitted
  ])

  if (typeof window !== "undefined") {
    useEffect(() => {
      const queryParams = new URLSearchParams(window?.location?.search)

      if (queryParams?.get("form") && showCallbackForm === false) {
        toggleCallbackForm()
      }

      let chatRetryInterval: number | undefined

      if (
        isLoggedIn &&
        userAssortmentPermissions?.chatPermission &&
        isLiveSupportLocale &&
        isPaid &&
        window?.sessionStorage?.getItem(SessionStorageKeys?.contactFormSlug)
      ) {
        const tryOpen = (): boolean => {
          if (openChat(dispatch)) {
            window?.sessionStorage?.removeItem(
              SessionStorageKeys?.contactFormSlug
            )
            return true
          }
          return false
        }

        if (!tryOpen()) {
          // NICE CXone guide button isn't in the DOM yet — poll until it is so
          // the user's auto-open intent isn't dropped on the floor.
          const startedAt = Date.now()
          chatRetryInterval = window.setInterval(() => {
            if (tryOpen() || Date.now() - startedAt > 15000) {
              window.clearInterval(chatRetryInterval)
            }
          }, 250)
        }
      } else if (
        isLoggedIn &&
        window?.sessionStorage?.getItem(SessionStorageKeys?.contactFormSlug)
      ) {
        queryParams?.set(
          QueryParameterKeys?.form,
          window?.sessionStorage?.getItem(
            SessionStorageKeys?.contactFormSlug
          ) as string
        )
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}?${queryParams.toString()}`
        )
        window?.sessionStorage?.removeItem(SessionStorageKeys?.contactFormSlug)
      }

      if (!queryParams?.get("form") && showCallbackForm === true) {
        toggleCallbackForm()
      }

      return () => {
        if (chatRetryInterval !== undefined) {
          window.clearInterval(chatRetryInterval)
        }
      }
    }, [
      userAssortmentPermissions,
      isLiveSupportLocale,
      userProfile,
      hasAttemptedLogin,
      isLoggedIn,
      window?.location?.href
    ])
  }

  if (typeof window !== "undefined") {
    useEffect(() => {
      if (
        window?.sessionStorage?.getItem(SessionStorageKeys?.contactFormSlug)
      ) {
        window?.scroll(0, 0)
      }
    }, [window?.sessionStorage])
  }

  const liveSupportMicrocopy = useMemo<LiveSupportChannelMicrocopy>(() => {
    const objMap = data?.liveSupportMicrocopy?.microcopySetEntries?.map(
      (entry) => Object.values(entry as { [key: string]: MicroCopy })
    )
    return Object.fromEntries(objMap as [string, MicroCopy][])
  }, [data?.liveSupportMicrocopy?.microcopySetEntries])

  const supportUnavailableMicrocopy =
    useMemo<LiveSupportUnavailableMicrocopy>(() => {
      const objMap =
        data?.actionComplexSupportUnavailable?.microcopySetEntries?.map(
          (entry) => Object.values(entry as { [key: string]: MicroCopy })
        )
      return Object.fromEntries(objMap as [string, MicroCopy][])
    }, [data?.actionComplexSupportUnavailable?.microcopySetEntries])

  const actionComplexMicroCopy = useMemo<ActionComplexMicrocopy>(() => {
    // const contactSubheader = isEn
    //   ? data?.contactActionComplexSubheaderEnglish?.microcopyValue?.raw
    //     ? documentToPlainTextString(
    //         JSON.parse(
    //           data.contactActionComplexSubheaderEnglish.microcopyValue.raw
    //         )
    //       )
    //     : "Explore your support options"
    //   : data?.contactActionComplexSubheader?.microcopyValue?.raw
    //   ? documentToPlainTextString(
    //       JSON.parse(data.contactActionComplexSubheader.microcopyValue.raw)
    //     )
    //   : "Explore your support options"

    const shouldUseFreeUserContent = !isPaid

    const contactHeader = data?.contactActionComplexHheader?.microcopyValue?.raw
      ? documentToPlainTextString(
          JSON.parse(data.contactActionComplexHheader.microcopyValue.raw)
        )
      : "Explore your support options"

    if (isPaid && !shouldUseFreeUserContent) {
      const objMap = data?.actionComplexProSub?.microcopySetEntries?.map(
        (entry) => Object.values(entry as { [key: string]: MicroCopy })
      )
      const microcopyObj: ActionComplexProSubMicrocopy = Object.fromEntries(
        objMap as [string, MicroCopy][]
      ) as ActionComplexProSubMicrocopy

      return {
        actionComplexHeader: microcopyObj?.selectionContactOptionsHeader?.raw
          ? documentToPlainTextString(
              JSON.parse(microcopyObj.selectionContactOptionsHeader.raw)
            )
          : contactHeader,
        actionComplexSubHeader: allLiveSupportIsTemporarilyDisabled
          ? documentToPlainTextString(
              JSON.parse(
                supportUnavailableMicrocopy
                  ?.messageSupportTemporarilyUnavailable?.raw as string
              )
            )
          : "",
        actionComplexCta: microcopyObj
          ?.selectionContactOptionsSubheaderPricingPackaging2dot1ChatCta?.raw
          ? documentToPlainTextString(
              JSON.parse(
                microcopyObj
                  .selectionContactOptionsSubheaderPricingPackaging2dot1ChatCta
                  .raw
              )
            )
          : "chatting with support."
      }
    }

    return {
      actionComplexHeader: contactHeader,
      // Temporarily commented out subheader until we land on the final design
      actionComplexSubHeader: "", // chatbotShouldRender ? "" : contactSubheader,
      actionComplexCta: "chatting with support."
    }
  }, [
    data?.actionComplexProSub?.microcopySetEntries,
    data?.contactActionComplexHheader?.microcopyValue?.raw,
    data?.contactActionComplexSubheader?.microcopyValue?.raw,
    data?.contactActionComplexSubheaderEnglish?.microcopyValue?.raw,
    userProfile,
    isPaid,
    isEn,
    urlParamFlag
  ])
  const disabledChannels = useMemo<DisabledChannels>(() => {
    return {
      chat: isDisabled(
        SupportChannelAPIKeys?.chat,
        userAssortmentPermissions?.temporarilyDisabled ?? []
      ),
      phone: isDisabled(
        SupportChannelAPIKeys?.phone,
        userAssortmentPermissions?.temporarilyDisabled ?? []
      ),
      email: isDisabled(
        SupportChannelAPIKeys?.email,
        userAssortmentPermissions?.temporarilyDisabled ?? []
      )
    }
  }, [userAssortmentPermissions?.temporarilyDisabled])

  const layoutProps = useMemo<PickedLayoutProps>(() => {
    return {
      microCopy: {
        buttonBrowseHelpCenter: data?.buttonBrowseHelpCenter,
        buttonContactUs: data?.buttonContactUs,
        messageContactUs: data?.messageContactUs,
        upperNavMicroCopySet: data?.upperNavMicroCopySet,
        headerSearchResults: data?.headerSearchResults,
        messageSearchBar: data?.messageSearchBar,
        chatbotUITranslations: data?.chatbotUITranslations
      },
      footerCopy: data?.contentfulFooter?.footerReference ?? null,
      footerNavCopy:
        data?.contentfulFooterNavigation?.FooterNavigationChildItems ?? null
    }
  }, [])

  const backCrumbTitle = useMemo(() => {
    if (
      data?.headerCopy?.microcopyValue?.raw &&
      data?.headerCopy?.microcopyValue?.raw?.length > 0
    ) {
      return documentToPlainTextString(
        JSON.parse(data?.headerCopy?.microcopyValue?.raw || "")
      )
    }

    return ""
  }, [data?.headerCopy?.microcopyValue])

  const userOrgs = useMemo<Options[]>(() => {
    if (userProfile?.allData?.orgData?.organizations) {
      return userProfile?.allData?.orgData?.organizations.map((org) => {
        return {
          label: org?.name,
          value: org?.id
        }
      })
    }
    return []
  }, [userProfile?.allData?.orgData?.organizations])

  const toggleOrg = useCallback((orgId: string) => {
    setSelectedOrgId(orgId)
  }, [])

  const showLiveChat = userAssortmentPermissions?.chatPermission

  useEffect(() => {
    if (showLiveChatGlobal !== showLiveChat) {
      dispatch(
        setMetaData({
          showLiveChatGlobal: showLiveChat
        })
      )
    }
  }, [showLiveChat])

  useEffect(() => {
    dispatch(
      setMetaData({
        allLiveSupportIsTemporarilyDisabled: allLiveSupportIsTemporarilyDisabled
      })
    )
  }, [allLiveSupportIsTemporarilyDisabled, dispatch])

  // Organizer tour
  const isOrganizer = userProfile?.isOrganizer
  const LS_KEY = "contactUsTourSeen"
  const TARGET_SEL = '[data-tour-step="contactus-organizer-tab"]'
  const [runTour, setRunTour] = useState(false)

  useEffect(() => {
    if (!isOrganizer) {
      setRunTour(false)
      return
    }

    const seen = localStorage.getItem(LS_KEY) === "1"
    if (seen) {
      setRunTour(false)
      return
    }

    // wait for the target to exist
    const el = document.querySelector(TARGET_SEL)
    setRunTour(Boolean(el))
  }, [isOrganizer])

  const onJoyride = ({ status }: CallBackProps) => {
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      localStorage.setItem(LS_KEY, "1")
      setRunTour(false)
    }
  }

  const organizerToolTipMicrocopy = useMemo(() => {
    const objMap = data?.organizerTabTooltip?.microcopySetEntries?.map(
      (entry) => Object.values(entry as { [key: string]: MicroCopy })
    )
    return Object.fromEntries(objMap as [string, MicroCopy][])
  }, [data?.organizerTabTooltip?.microcopySetEntries])

  const toolTipHeader = renderRichText(
    organizerToolTipMicrocopy?.headerTooltipOrganizingEvents?.raw,
    customRendererToRemovePTag
  )
  const toolTipContent = renderRichText(
    organizerToolTipMicrocopy?.messageTooltipOrganizerTopics?.raw,
    customRendererToRemovePTag
  )

  const contactSteps = [
    {
      target: TARGET_SEL,
      title: toolTipHeader ?? "Looks like you organize events",
      content: toolTipContent ?? "These topics might help.",
      placement: "bottom" as const,
      placementBeacon: "right" as const,
      hidePrimary: true
    }
  ]

  const contactStyles = {
    ...tourGuideStyles,
    beacon: {
      marginLeft: 20
    },
    beaconInner: { backgroundColor: "#F05537" },
    beaconOuter: { borderColor: "#F05537" },
    buttonClose: { display: "none" },
    options: { beaconSize: 12 }
  }

  return (
    <>
      {showCallbackForm ? (
        <ContactForm
          gatsbyPageProps={pageProps}
          handleBackBtnClick={toggleCallbackForm}
          setCaseId={setCaseId as Dispatch<SetStateAction<string>>}
          setCallbackFormSubmitted={setCallbackFormSubmitted}
          layoutProps={layoutProps}
          backCrumbTitle={backCrumbTitle}
          contactForms={data?.contactForms}
          nodeLocale={params?.node_locale}
        />
      ) : (
        <>
          <Layout
            gatsbyPageProps={pageProps}
            searchBar={{ isSearchBarVisible: true, isResultsBoxShown: true }}
            contact={true}
            page="Browse Help Center"
            {...layoutProps}
          >
            <div className={styles?.innerContainer}>
              <div className={styles?.innerContentContainerLeft}>
                <BackCrumb
                  url="/"
                  nodeLocale={params?.node_locale}
                  title={
                    data?.navigationHomepage?.microcopyValue?.raw
                      ? documentToPlainTextString(
                          JSON.parse(
                            data?.navigationHomepage?.microcopyValue?.raw
                          )
                        )
                      : ""
                  }
                />

                <Typography
                  className={styles?.heading}
                  as="h1"
                  variant="heading-md"
                >
                  {renderRichText(
                    data?.headerCopy?.microcopyValue?.raw as string,
                    customRendererToRemovePTag
                  )}
                </Typography>

                <InlineAlert className={styles?.inlineAlertContainer} />
                {!hasAttemptedLogin && (
                  <div className={styles.animatedSpinnerContainer}>
                    <Lottie
                      options={{
                        loop: true,
                        autoplay: true,
                        animationData: greenLoaderLottieData,
                        rendererSettings: {
                          preserveAspectRatio: "xMidYMid slice"
                        }
                      }}
                      width={24}
                      height={24}
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)"
                      }}
                    />
                    <LogInLegacy
                      loginButtonMicrocopy={data?.loginButtonMicrocopy}
                      loginContentMicrocopy={data?.loginContentMicrocopy}
                      loginHeaderMicrocopy={data?.loginHeaderMicrocopy}
                      cantLogInMicrocopy={data?.cantLogInMicrocopy}
                      cantLogInArticle={data?.cantLogInArticle}
                      containerClassName={styles.invisibleLoginComponent}
                    />
                  </div>
                )}

                {!isLoggedIn && hasAttemptedLogin && (
                  <div className={`${styles.animatedSpinnerContainerOther} `}>
                    <LogInLegacy
                      loginButtonMicrocopy={data?.loginButtonMicrocopy}
                      loginContentMicrocopy={data?.loginContentMicrocopy}
                      loginHeaderMicrocopy={data?.loginHeaderMicrocopy}
                      cantLogInMicrocopy={data?.cantLogInMicrocopy}
                      cantLogInArticle={data?.cantLogInArticle}
                      containerClassName={styles.visibleLoginComponent}
                    />
                  </div>
                )}

                {/* If user is in experiment, show chatbot only (what regular attendees see) */}
                {showChatbotFirst ? (
                  <>
                    {isLoggedIn && (
                      <>
                        <ActionComplex microCopy={actionComplexMicroCopy}>
                          {/* EB AI widget */}
                          <SupportWidget
                            icon={<ChatIcon size="medium" />}
                            primaryText={
                              (
                                renderRichText(
                                  liveSupportMicrocopy?.chatTitle?.raw
                                ) as any
                              )[0]?.props?.children?.[0] ??
                              "Chat with Eventbrite"
                            }
                            secondaryText={
                              (
                                renderRichText(
                                  liveSupportMicrocopy
                                    ?.selectionDescriptionAvailability247?.raw
                                ) as any
                              )[0]?.props?.children?.[0] ?? "Available 24/7"
                            }
                            clickHandler={(e) => {
                              e.preventDefault()

                              if (typeof window !== "undefined") {
                                if (window?.duet?.open) {
                                  window.duet.open()
                                }
                              }
                            }}
                          />
                        </ActionComplex>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {/* If user is NOT in experiment, show regular contact options based on tier */}
                    {/* show AI chat if user does not qualify for live chat or all live support is temporarily disabled */}
                    {!showLiveChat || allLiveSupportIsTemporarilyDisabled ? (
                      <>
                        {isLoggedIn &&
                        (!isLiveSupportLocale ||
                          userProfile?.isAttendee ||
                          allLiveSupportIsTemporarilyDisabled) ? (
                          <>
                            <ActionComplex microCopy={actionComplexMicroCopy}>
                              {/* EB AI widget */}
                              <SupportWidget
                                icon={<ChatIcon size="medium" />}
                                primaryText={
                                  (
                                    renderRichText(
                                      liveSupportMicrocopy?.chatTitle?.raw
                                    ) as any
                                  )[0]?.props?.children?.[0] ??
                                  "Chat with Eventbrite"
                                }
                                secondaryText={
                                  (
                                    renderRichText(
                                      liveSupportMicrocopy
                                        ?.selectionDescriptionAvailability247
                                        ?.raw
                                    ) as any
                                  )[0]?.props?.children?.[0] ?? "Available 24/7"
                                }
                                clickHandler={(e) => {
                                  e.preventDefault()

                                  if (typeof window !== "undefined") {
                                    if (window?.duet?.open) {
                                      window.duet.open()
                                    }
                                  }
                                }}
                              />
                            </ActionComplex>
                          </>
                        ) : null}
                      </>
                    ) : null}

                    {showLiveChat && !allLiveSupportIsTemporarilyDisabled && (
                      <ActionComplex microCopy={actionComplexMicroCopy}>
                        {/* Chat widget */}
                        {userAssortmentPermissions?.chatPermission && (
                          <LiveChat
                            primaryText={
                              disabledChannels?.chat
                                ? ((
                                    renderRichText(
                                      supportUnavailableMicrocopy
                                        ?.selectionChatUnavailableHeader?.raw
                                    ) as any
                                  )[0]?.props?.children?.[0] ??
                                  "Chat unavailable")
                                : ((
                                    renderRichText(
                                      liveSupportMicrocopy
                                        ?.selectionContactChatHeader?.raw
                                    ) as any
                                  )[0]?.props?.children?.[0] ??
                                  "Chat with support")
                            }
                            secondaryText={
                              disabledChannels?.chat ? (
                                ((
                                  renderRichText(
                                    supportUnavailableMicrocopy
                                      ?.selectionDescriptionChatUnavailable?.raw
                                  ) as any
                                )[0]?.props?.children?.[0] ??
                                "Chat is temporarily unavailable. Please try again later.")
                              ) : (
                                <>
                                  {(
                                    renderRichText(
                                      liveSupportMicrocopy
                                        ?.selectionDescriptionChatAvailableAlways
                                        ?.raw
                                    ) as any
                                  )[0]?.props?.children?.[0] ??
                                    "Available 24/7"}
                                  <br />
                                  {(
                                    renderRichText(
                                      liveSupportMicrocopy
                                        ?.selectionDescriptionChatWaitTimeStatic
                                        ?.raw
                                    ) as any
                                  )[0]?.props?.children?.[0] ??
                                    "Wait: 2 minutes"}
                                </>
                              )
                            }
                            icon={<ChatIcon size="medium" />}
                            recommended={multipleContactChannels ? true : false}
                            withinBusinessHours={withinChatHours}
                            disabled={disabledChannels?.chat}
                            order={disabledChannels?.chat ? 1 : 0}
                          />
                        )}

                        {/* Request a call back widget */}

                        {(userAssortmentPermissions?.phonePermission ||
                          userProfile?.isSalesCreator) &&
                          currentLangCode.toLowerCase().includes("en") && (
                            <SupportWidget
                              classes={classNames({
                                [styles?.disabledSupportOption]:
                                  !withinPhoneHours || disabledChannels?.phone
                              })}
                              primaryText={
                                withinPhoneHours
                                  ? disabledChannels?.phone
                                    ? ((
                                        renderRichText(
                                          supportUnavailableMicrocopy
                                            ?.selectionCallbackUnavailableHeader
                                            ?.raw
                                        ) as any
                                      )[0]?.props?.children?.[0] ??
                                      "Callback unavailable")
                                    : ((
                                        renderRichText(
                                          liveSupportMicrocopy
                                            ?.selectionContactCallbackHeader
                                            ?.raw
                                        ) as any
                                      )[0]?.props?.children?.[0] ??
                                      "Request a callback")
                                  : "Callback unavailable"
                              }
                              secondaryText={
                                <>
                                  {disabledChannels?.phone
                                    ? ((
                                        renderRichText(
                                          supportUnavailableMicrocopy
                                            ?.selectionDescriptionCallbackUnavailable
                                            ?.raw
                                        ) as any
                                      )[0]?.props?.children?.[0] ??
                                      "Request a callback is temporarily unavailable.")
                                    : ((
                                        renderRichText(
                                          liveSupportMicrocopy
                                            ?.selectionDescriptionAvailabilityWeekdayBusinessHours
                                            ?.raw
                                        ) as any
                                      )[0]?.props?.children?.[0] ??
                                      "Request a callback is temporarily unavailable.")}
                                  {withinPhoneHours ? (
                                    disabledChannels?.phone ? null : (
                                      <>
                                        <br />
                                        {(
                                          renderRichText(
                                            liveSupportMicrocopy
                                              ?.selectionDescriptionCallbackWaitTimeStatic
                                              ?.raw
                                          ) as any
                                        )[0]?.props?.children?.[0] ??
                                          "Wait: Within 25 minutes"}
                                      </>
                                    )
                                  ) : (
                                    ""
                                  )}
                                </>
                              }
                              clickHandler={() =>
                                disabledChannels?.phone
                                  ? null
                                  : callbackClickHandler()
                              }
                              icon={<PhoneIcon size="medium" />}
                              tabIndex={
                                userAssortmentPermissions?.phonePermission ||
                                userProfile?.isSalesCreator
                                  ? 0
                                  : -1
                              }
                              order={disabledChannels?.phone ? 1 : 0}
                            />
                          )}
                        {/* consultation widget. Leaving here in case we decide to turn this back on for any reason. 12/09/2025 */}
                        {userAssortmentPermissions?.consultation === true &&
                          currentLangCode.toLowerCase().includes("en") && (
                            <SupportWidget
                              primaryText={
                                (
                                  renderRichText(
                                    liveSupportMicrocopy
                                      ?.selectionContactScheduleSessionHeader
                                      ?.raw
                                  ) as any
                                )[0]?.props?.children?.[0] ??
                                "Schedule a session"
                              }
                              secondaryText={
                                (
                                  renderRichText(
                                    liveSupportMicrocopy
                                      ?.selectionDescriptionOnboardingSession
                                      ?.raw
                                  ) as any
                                )?.[0]?.props?.children?.[0] ??
                                "Need help launching your event? Get advice on event settings and how to start promoting."
                              }
                              icon={<MegaphoneIcon size="medium" />}
                              clickHandler={() => goToGrooveCalender(onboard)}
                            />
                          )}

                        {/* Formerly Flex email widget. Leaving here in case we decide to turn on this widget for
                    a different cohort. 08/21/2024 sd-1991 */}
                        {userAssortmentPermissions?.emailPermission && (
                          <SupportWidget
                            primaryText={
                              (
                                renderRichText(
                                  liveSupportMicrocopy
                                    ?.selectionContactEmailHeader?.raw
                                ) as any
                              )[0]?.props?.children?.[0] ??
                              "Email customer support"
                            }
                            secondaryText={
                              (
                                renderRichText(
                                  liveSupportMicrocopy
                                    ?.selectionEmailContactDescription?.raw
                                ) as any
                              )[0]?.props?.children?.[0] ??
                              "Estimated wait: 1-3 business days"
                            }
                            icon={<MailIcon size="medium" />}
                            clickHandler={() => {
                              navigateWithPrefix(`/contact-us/?form=org-email`)
                            }}
                            order={
                              isDisabled(
                                SupportChannelAPIKeys?.email,
                                userAssortmentPermissions?.temporarilyDisabled
                              )
                                ? 1
                                : 0
                            }
                          />
                        )}
                      </ActionComplex>
                    )}
                  </>
                )}

                <div className={styles?.innerContentContainerRight}>
                  <PersonalizationSection
                    mode="contact-us"
                    columnLayout={!isTabletOrLarger}
                    messageNoUpcomingOrders={data?.messageNoUpcomingOrders}
                    noTicketsFoundActionComplex={
                      data?.noTicketsFoundActionComplex
                    }
                    ticketsFoundActionComplex={data?.ticketsFoundActionComplex}
                    searchingForTickets={data?.searchingForTickets}
                    noEventsFoundActionComplex={
                      data?.noEventsFoundActionComplex
                    }
                    eventsFoundActionComplex={data?.eventsFoundActionComplex}
                    searchingForEvents={data?.searchingForEvents}
                    messageNoUpcomingEvents={data?.messageNoUpcomingEvents}
                    loginButtonMicrocopy={data?.loginButtonMicrocopy}
                    loginContentMicrocopy={data?.loginContentMicrocopy}
                    loginHeaderMicrocopy={data?.loginHeaderMicrocopy}
                  />
                </div>
                {runTour && (
                  <Joyride
                    steps={contactSteps}
                    run={runTour}
                    tooltipComponent={JoyrideTourGuide}
                    styles={contactStyles}
                    floaterProps={{
                      styles: JOYRIDE_TOURGUIDE_FLOATER_STYLES_DEFAULT
                    }}
                    disableOverlay
                    continuous
                    locale={{ last: "" }}
                    hideBackButton
                    callback={onJoyride}
                  />
                )}
                <SupportOptions
                  userOrgs={userOrgs}
                  currentOrg={currentOrg}
                  toggleOrg={toggleOrg}
                  isTogglingOrg={isTogglingOrg}
                />
                <CommonQuestionsSection
                  data={data?.contentfulLandingPage}
                  audienceQuestionsSectionMicroCopy={
                    data?.audienceQuestionMicroCopy
                  }
                  messageLoginContactUsMicroCopy={
                    data?.messageLoginContactUsMicroCopy
                  }
                  chatProvisioned={
                    userAssortmentPermissions?.chatPermission ?? false
                  }
                  withinChatBusinessHours={withinChatHours}
                />
              </div>

              <div className={styles?.innerContentContainerRight}>
                <PersonalizationSection
                  mode="contact-us"
                  columnLayout={true}
                  messageNoUpcomingOrders={data?.messageNoUpcomingOrders}
                  noTicketsFoundActionComplex={
                    data?.noTicketsFoundActionComplex
                  }
                  ticketsFoundActionComplex={data?.ticketsFoundActionComplex}
                  searchingForTickets={data?.searchingForTickets}
                  noEventsFoundActionComplex={data?.noEventsFoundActionComplex}
                  eventsFoundActionComplex={data?.eventsFoundActionComplex}
                  searchingForEvents={data?.searchingForEvents}
                  messageNoUpcomingEvents={data?.messageNoUpcomingEvents}
                  loginButtonMicrocopy={data?.loginButtonMicrocopy}
                  loginContentMicrocopy={data?.loginContentMicrocopy}
                  loginHeaderMicrocopy={data?.loginHeaderMicrocopy}
                />
              </div>
            </div>
          </Layout>
        </>
      )}
    </>
  )
}

export const Head: HeadFC<
  Queries.CommonIssuesAndQuestionsQuery,
  PageContext
> = (headArgs) => {
  const canonicalUrl = useMemo<string>(() => {
    if (headArgs?.location?.pathname?.length > 0) {
      return headArgs?.location?.pathname
    }

    return ""
  }, [headArgs])

  const shouldNoIndex = useMemo(
    () =>
      !headArgs?.data?.contentfulLandingPage?.landingPageSeoMetadata
        ?.seoMetaTagRobotsIndex,
    [headArgs?.data]
  )

  return (
    <>
      <HeadUniversal
        {...headArgs}
        metaTitle={
          headArgs?.data?.contentfulLandingPage?.landingPageSeoMetadata
            ?.seoMetaTagTitle
        }
        metaDescription={
          headArgs?.data?.contentfulLandingPage?.landingPageSeoMetadata
            ?.seoMetaTagDescription
        }
      />
      {getTitleText(
        headArgs?.data?.contentfulLandingPage?.landingPageSeoMetadata
          ?.seoMetaTagTitle || ""
      )}
      {generateDescriptionMetaTag(
        headArgs?.data?.contentfulLandingPage?.landingPageSeoMetadata
          ?.seoMetaTagDescription || ""
      )}
      {generateRobotsMetaTag(
        headArgs?.data?.contentfulLandingPage?.landingPageSeoMetadata
          ?.seoMetaTagRobotsIndex || false,
        headArgs?.data?.contentfulLandingPage?.landingPageSeoMetadata
          ?.seoMetaTagCrawlerFollow || false
      )}
      <HrefLangTags
        metaData={staticHrefLangTags("contact-us")}
        shouldNoIndex={shouldNoIndex}
      />
      <CanonicalTag
        href={canonicalUrl}
        nodeLocale={headArgs?.pageContext?.node_locale}
        shouldNoIndex={shouldNoIndex}
      />
    </>
  )
}

export const query = graphql`
  query CommonIssuesAndQuestions($node_locale: String!) {
    # LOGIN
    loginContentMicrocopy: contentfulMicrocopy(
      microcopyKey: { eq: "messageLoginResources" }
      node_locale: { eq: $node_locale }
    ) {
      contentful_id
      node_locale
      microcopyInternalTitle
      microcopyKey
      microcopyValue {
        raw
      }
    }

    loginHeaderMicrocopy: contentfulMicrocopy(
      microcopyKey: { eq: "headerLoginResources" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyValue {
        raw
      }
    }

    cantLogInMicrocopy: contentfulMicrocopy(
      microcopyKey: { eq: "navigationCantLogIn" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyValue {
        raw
      }
    }

    cantLogInArticle: contentfulHelpArticle(
      articleId: { eq: 221530 }
      node_locale: { eq: $node_locale }
    ) {
      articleId
      slug
    }

    loginButtonMicrocopy: contentfulMicrocopy(
      microcopyKey: { eq: "navigationLogIn" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyValue {
        raw
      }
    }
    # END LOGIN

    # PERSONALIZATION
    # FIND MY TICKETS
    messageNoUpcomingOrders: contentfulMicrocopy(
      microcopyKey: { eq: "messageNoUpcomingOrders" }
      node_locale: { eq: $node_locale }
    ) {
      contentful_id
      microcopyValue {
        raw
      }
    }

    searchingForTickets: contentfulEventActionComplex(
      contentful_id: { eq: "HGMxLdiIS4NgziflHZXtU" }
      node_locale: { eq: $node_locale }
    ) {
      id
      node_locale
      eventActionComplexInternalTitle
      eventActionComplexLoadingMessage {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
    }

    ticketsFoundActionComplex: contentfulEventActionComplex(
      contentful_id: { eq: "7qfRoL77Rw1J9AFa7IcKnv" }
      node_locale: { eq: $node_locale }
    ) {
      id
      node_locale
      eventActionComplexInternalTitle
      eventActionComplexHeader
      eventActionComplexPrimaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexSecondaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexTertiaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexOtherOptions {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
    }

    noTicketsFoundActionComplex: contentfulEventActionComplex(
      contentful_id: { eq: "4VjzGzF1EYJKdWthSHYRrH" }
      node_locale: { eq: $node_locale }
    ) {
      id
      node_locale
      eventActionComplexInternalTitle
      eventActionComplexHeader
      eventActionComplexPrimaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexSecondaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexTertiaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexOtherOptions {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
    }
    # END FIND MY TICKETS

    # FIND MY EVENTS
    messageNoUpcomingEvents: contentfulMicrocopy(
      microcopyKey: { eq: "messageNoUpcomingEvents" }
      node_locale: { eq: $node_locale }
    ) {
      contentful_id
      microcopyValue {
        raw
      }
    }

    searchingForEvents: contentfulEventActionComplex(
      contentful_id: { eq: "7dL10FjtyQxQglQ0RH1tos" }
      node_locale: { eq: $node_locale }
    ) {
      id
      node_locale
      eventActionComplexInternalTitle
      eventActionComplexLoadingMessage {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
    }

    noEventsFoundActionComplex: contentfulEventActionComplex(
      contentful_id: { eq: "6DJb2f0Kj2xvTlsFsTx0Yd" }
      node_locale: { eq: $node_locale }
    ) {
      id
      node_locale
      eventActionComplexInternalTitle
      eventActionComplexHeader
      eventActionComplexPrimaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexSecondaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexTertiaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexOtherOptions {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
    }

    eventsFoundActionComplex: contentfulEventActionComplex(
      contentful_id: { eq: "63OCAocrPDWrso7ye5VBdp" }
      node_locale: { eq: $node_locale }
    ) {
      id
      node_locale
      eventActionComplexInternalTitle
      eventActionComplexHeader
      eventActionComplexPrimaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexSecondaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexTertiaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexOtherOptions {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
    }
    # END FIND MY EVENTS
    # END PERSONALIZATION

    chatbotUITranslations: allContentfulMicrocopySet(
      filter: {
        microcopySetInternalName: { eq: "Chatbot UI" }
        node_locale: { eq: $node_locale }
      }
    ) {
      edges {
        node {
          node_locale
          microcopySetEntries {
            ... on ContentfulMicrocopy {
              contentful_id
              microcopyKey
              microcopyValue {
                raw
              }
            }
          }
        }
      }
    }

    messageLoginContactUsMicroCopy: contentfulMicrocopy(
      microcopyKey: { eq: "messageLoginContactUs" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyValue {
        raw
      }
    }

    audienceQuestionMicroCopy: contentfulMicrocopySet(
      microcopySetInternalName: { eq: "Contact page" }
      node_locale: { eq: $node_locale }
    ) {
      microcopySetEntries {
        ... on ContentfulMicrocopy {
          microcopyKey
          microcopyValue {
            raw
          }
        }
      }
    }

    # ORGANIZER TAB TOOL TIP
    organizerTabTooltip: contentfulMicrocopySet(
      microcopySetInternalName: { eq: "Tooltip for organizer help topics" }
      node_locale: { eq: $node_locale }
    ) {
      microcopySetInternalName
      microcopySetEntries {
        ... on ContentfulMicrocopy {
          microcopyKey
          microcopyValue {
            raw
          }
        }
      }
    }
    # END ORGANIZER TAB TOOL TIP

    # SUPPORT OPTIONS COMPONENT FIELDS NON-ENGLISH
    supportOptionsMicroCopySetNonEnglish: contentfulMicrocopySet(
      microcopySetInternalName: {
        eq: "Contact page: Your support options (non-English)"
      }
      node_locale: { eq: $node_locale }
    ) {
      microcopySetInternalName
      microcopySetEntries {
        ... on ContentfulMicrocopy {
          microcopyKey
          microcopyValue {
            raw
          }
        }
      }
    }
    # END SUPPORT COMPONENT FIELDS NON-ENGLISH

    # BREADCRUMB FIELDS
    navigationHomepage: contentfulMicrocopy(
      microcopyKey: { eq: "navigationHomepage" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    # END BREADCRUMB FIELDS

    # LAYOUT FIELDS
    messageSearchBar: contentfulMicrocopy(
      microcopyKey: { eq: "messageSearchBar" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    headerSearchResults: contentfulMicrocopy(
      microcopyKey: { eq: "headerSearchResults" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }

    buttonContactUs: contentfulMicrocopy(
      microcopyKey: { eq: "buttonContactUs" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    buttonBrowseHelpCenter: contentfulMicrocopy(
      microcopyKey: { eq: "buttonBrowseHelpCenter" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    messageContactUs: contentfulMicrocopy(
      microcopyKey: { eq: "messageStillHaveQuestions" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }

    upperNavMicroCopySet: contentfulMicrocopySet(
      microcopySetInternalName: { eq: "Help Center nav" }
      node_locale: { eq: $node_locale }
    ) {
      microcopySetEntries {
        ... on ContentfulMicrocopy {
          microcopyKey
          microcopyValue {
            raw
          }
        }
      }
    }

    contentfulFooter(node_locale: { eq: $node_locale }) {
      id
      footerInternalName
      footerReference {
        displayText
        FooterNavigationChildItems {
          footerNavigationInternalTitle
          displayText
          footerNavigationUrl
        }
      }
    }
    contentfulFooterNavigation(
      footerNavigationInternalTitle: { eq: "Category: Bottom row navigation" }
      node_locale: { eq: $node_locale }
    ) {
      footerNavigationInternalTitle
      footerNavigationUrl
      FooterNavigationChildItems {
        footerNavigationInternalTitle
        displayText
        footerNavigationUrl
      }
    }
    # END LAYOUT FIELDS

    # ACTION COMPLEX MICROCOPY

    actionComplexProSub: contentfulMicrocopySet(
      microcopySetInternalName: {
        eq: "Contact page: Action complex chat (Pricing and packaging 2.1)"
      }
      node_locale: { eq: $node_locale }
    ) {
      microcopySetEntries {
        ... on ContentfulMicrocopy {
          microcopyKey
          microcopyValue {
            raw
          }
        }
      }
    }

    actionComplexSupportUnavailable: contentfulMicrocopySet(
      microcopySetInternalName: {
        eq: "Contact page: Support temporarily unavailable"
      }
      node_locale: { eq: $node_locale }
    ) {
      microcopySetEntries {
        ... on ContentfulMicrocopy {
          microcopyKey
          microcopyValue {
            raw
          }
        }
      }
    }

    actionComplexFlexCopy: contentfulMicrocopySet(
      microcopySetInternalName: {
        eq: "Contact page: Action complex email (Pricing and packaging 2.1)"
      }
      node_locale: { eq: $node_locale }
    ) {
      microcopySetEntries {
        ... on ContentfulMicrocopy {
          microcopyKey
          microcopyValue {
            raw
          }
        }
      }
    }

    liveSupportMicrocopy: contentfulMicrocopySet(
      microcopySetInternalName: { eq: "Contact page: Action complex" }
      node_locale: { eq: $node_locale }
    ) {
      microcopySetEntries {
        ... on ContentfulMicrocopy {
          microcopyKey
          microcopyValue {
            raw
          }
        }
      }
    }

    supportOptionsPricingAndPackaginMicrocopy: contentfulMicrocopySet(
      microcopySetInternalName: {
        eq: "Contact page: Your support options (Pricing and packaging 2.1)"
      }
      node_locale: { eq: $node_locale }
    ) {
      microcopySetEntries {
        ... on ContentfulMicrocopy {
          microcopyKey
          microcopyValue {
            raw
          }
        }
      }
    }

    # END ACTION COMPLEX MICROCOPY

    # ASSORTMENT PERMISSIONS
    assortmentPermissions: contentfulCustomSettingAssortmentPermission {
      assortments: assortmentPermissionsReference {
        assortmentName: assortmentPermissionName
        chatPermission
        consultation
        emailPermission
        phonePermission
        temporarilyDisabled
      }
    }

    # END ASSORTMENT PERMISSIONS

    # SUPPORTED LOCALES

    liveSupportProvisionedRegions: contentfulCustomSettingLiveSupportProvisionedRegions {
      supportedLocales
    }
    # END SUPPORTED LOCALES

    # SUPPORT LISTS
    supportLists: allContentfulCustomSettingSupportListVariation(
      filter: { node_locale: { eq: $node_locale } }
    ) {
      edges {
        node {
          supportListLocale
          localeCode
          supportListUrl
          supportOfficeCity {
            Sunday: businessHoursSunday
            Monday: businessHoursMonday
            Tuesday: businessHoursTuesday
            Wednesday: businessHoursWednesday
            Thursday: businessHoursThursday
            Friday: businessHoursFriday
            Saturday: businessHoursSaturday
            businessHoursTimeZone
            locale
          }
          premiumSupportOfficeCity {
            Sunday: businessHoursSunday
            Monday: businessHoursMonday
            Tuesday: businessHoursTuesday
            Wednesday: businessHoursWednesday
            Thursday: businessHoursThursday
            Friday: businessHoursFriday
            Saturday: businessHoursSaturday
            businessHoursTimeZone
            locale
          }
          phoneSupportOfficeCity {
            Sunday: businessHoursSunday
            Monday: businessHoursMonday
            Tuesday: businessHoursTuesday
            Wednesday: businessHoursWednesday
            Thursday: businessHoursThursday
            Friday: businessHoursFriday
            Saturday: businessHoursSaturday
            businessHoursTimeZone
            locale
          }
        }
      }
    }

    # END SUPPORT LISTS

    # CALL BACK MICROCOPY

    # phoneBizHours: contentfulMicrocopy(
    #   node_locale: { eq: $node_locale }
    #   microcopyKey: { eq: "callbackBusinessHours" }
    # ) {
    #   microcopyKey
    #   microcopyValue {
    #     raw
    #   }
    # }

    # phoneSLA: contentfulMicrocopy(
    #   node_locale: { eq: $node_locale }
    #   microcopyKey: { eq: "callbackSla" }
    # ) {
    #   microcopyKey
    #   microcopyValue {
    #     raw
    #   }
    # }

    # END CALL BACK MICROCOPY

    contentfulLandingPage(
      internalHomepageTitle: { eq: "Contact us (Common issues and questions)" }
      node_locale: { eq: $node_locale }
    ) {
      title: homepageTitle
      default: landingPageDefaultUser {
        sectionTitle: landingPageVariationTitle
        content: landingPageVariationContent {
          ... on ContentfulContactFaqCategory {
            faqCategoryHeader
            sections: allSelections {
              isCodeRed
              promoteChat: contactSummaryProvisionedChannel
              isContactFormAvailableLoggedOut
              title: contactSummarySelectionTitle
              body: contactSummaryCustomBodyCopy {
                raw
              }

              contactFormUrl
              contactForm: contactSummaryFormCopyReference {
                contactFormUrl
              }
              contactFormCtaCopy {
                microcopyValue {
                  raw
                }
              }
              contactFormCtaCopyProvisioned {
                microcopyValue {
                  raw
                }
              }
              contactSummaryRelatedArticles {
                articleId
                helpArticleTitle
                slug
              }
            }
          }
        }
      }
      attending: landingPageAttendee {
        sectionTitle: landingPageVariationTitle
        content: landingPageVariationContent {
          ... on ContentfulContactFaqCategory {
            faqCategoryHeader
            sections: allSelections {
              isCodeRed
              promoteChat: contactSummaryProvisionedChannel
              isContactFormAvailableLoggedOut
              title: contactSummarySelectionTitle
              body: contactSummaryCustomBodyCopy {
                raw
              }
              contactFormUrl
              contactForm: contactSummaryFormCopyReference {
                contactFormUrl
              }
              contactFormCtaCopy {
                microcopyValue {
                  raw
                }
              }
              contactFormCtaCopyProvisioned {
                microcopyValue {
                  raw
                }
              }
              contactSummaryRelatedArticles {
                articleId
                helpArticleTitle
                slug
              }
            }
          }
        }
      }
      organizing: landingPageOrganizer {
        sectionTitle: landingPageVariationTitle
        content: landingPageVariationContent {
          ... on ContentfulContactFaqCategory {
            faqCategoryHeader
            sections: allSelections {
              title: contactSummarySelectionTitle
              isCodeRed
              promoteChat: contactSummaryProvisionedChannel
              isContactFormAvailableLoggedOut
              body: contactSummaryCustomBodyCopy {
                raw
              }
              contactFormUrl
              contactForm: contactSummaryFormCopyReference {
                contactFormUrl
              }
              contactFormCtaCopy {
                microcopyValue {
                  raw
                }
              }
              contactFormCtaCopyProvisioned {
                microcopyValue {
                  raw
                }
              }
              contactSummaryRelatedArticles {
                articleId
                helpArticleTitle
                slug
              }
            }
          }
        }
      }
      somethingElse: landingPageSomethingElse {
        sectionTitle: landingPageVariationTitle
        content: landingPageVariationContent {
          ... on ContentfulContactFaqCategory {
            faqCategoryHeader
            sections: allSelections {
              title: contactSummarySelectionTitle
              isCodeRed
              promoteChat: contactSummaryProvisionedChannel
              isContactFormAvailableLoggedOut
              body: contactSummaryCustomBodyCopy {
                raw
              }
              contactFormUrl
              contactForm: contactSummaryFormCopyReference {
                contactFormUrl
              }
              contactFormCtaCopy {
                microcopyValue {
                  raw
                }
              }
              contactFormCtaCopyProvisioned {
                microcopyValue {
                  raw
                }
              }
              contactSummaryRelatedArticles {
                articleId
                helpArticleTitle
                slug
              }
            }
          }
        }
      }
      landingPageSeoMetadata {
        seoMetaTagCrawlerFollow
        seoMetaTagDescription
        seoMetaTagRobotsIndex
        seoMetaTagTitle
      }
    }
    requestCallBackProps: contentfulContactForm(
      contactFormInternalTitle: { eq: "Form D: Request a callback" }
      node_locale: { eq: $node_locale }
    ) {
      contactFormUrl
    }
    headerCopy: contentfulMicrocopy(
      microcopyKey: { eq: "headerContactUs" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyValue {
        raw
      }
    }
    contactForms: allContentfulContactForm(
      filter: { node_locale: { eq: $node_locale } }
    ) {
      edges {
        node {
          node_locale
          header: contactFormHeader {
            value: microcopyValue {
              raw
            }
          }
          subheader: contactFormSubheader {
            value: microcopyValue {
              raw
            }
          }
          contactFormUrl
          sections: contactFormSectionsReference {
            sectionHeader: contactFormSectionHeader
            internalTitle: contactFormSectionInternalTitle
            fields: contactFormSectionFields {
              ... on ContentfulContactFormField {
                fieldName: contactFormFieldName
                internalTitle: contactFormFieldInternalTitle
                isRequired: contactFormFieldRequiredBoolean
                helperText: contactFormFieldHelperText
                isRadioButton
                radioButtonOptions
                errorMessage: contactFormErrorMessages {
                  errorMessageText
                }
                isDropdownMenu
                dropdownMenuOptions {
                  routingTopicInternalTitle
                  routingTopicSelection
                  routingTopicSalesforceApiName
                  routingTopicChildItems {
                    ... on ContentfulRoutingSubtopic {
                      routingSubtopicSelection
                      routingSubtopicSalesforceApiName
                    }
                  }
                }
              }
            }
          }
          Disclaimers: contactFormDisclaimers {
            microcopyKey
            microcopyValue {
              raw
            }
          }
          contactFormSubjectLine
          contactFormButtons {
            microcopySetEntries {
              ... on ContentfulMicrocopy {
                microcopyKey
                microcopyValue {
                  raw
                }
              }
            }
          }
          contactFormGraphic {
            url
          }
        }
      }
    }
    # ERROR MESSAGE FOR PRIVACY POLICY CHECKBOX
    agreeToTermsError: contentfulErrorMessage(
      contentful_id: { eq: "PMcWc87X50shwVhaePA0I" }
      node_locale: { eq: $node_locale }
    ) {
      contentful_id
      errorMessageText
    }

    # CHAT ROUTING TOPICS
    contentfulRoutingTopic(
      routingTopicInternalTitle: { eq: "Routing topics - Chat" }
      node_locale: { eq: $node_locale }
    ) {
      routingTopicInternalTitle
      routingTopicChildItems {
        ... on ContentfulRoutingTopic {
          routingTopicSalesforceApiName
          routingTopicSelection
        }
      }
    }
    # END CHAT ROUTING TOPICS

    # Action Complex Subheader
    contactActionComplexSubheader: contentfulMicrocopy(
      microcopyInternalTitle: {
        eq: "Message: Your support options, non English-speaking locale"
      }
      node_locale: { eq: $node_locale }
    ) {
      contentful_id
      microcopyKey
      microcopyValue {
        raw
      }
    }

    contactActionComplexSubheaderEnglish: contentfulMicrocopy(
      microcopyInternalTitle: {
        eq: "Message: Your support options, non-provisioned user with Eventbrite AI"
      }
      node_locale: { eq: $node_locale }
    ) {
      contentful_id
      microcopyKey
      microcopyValue {
        raw
      }
    }
    #End Action Complex Subheader

    # Action Complex Header

    contactActionComplexHheader: contentfulMicrocopy(
      microcopyInternalTitle: { eq: "Header: Explore your support options" }
      node_locale: { eq: $node_locale }
    ) {
      contentful_id
      microcopyKey
      microcopyValue {
        raw
      }
    }
    # End of Action Complex Header

    #Success message for click to call submission
    successAlertCallback: contentfulAlert(
      alertInternalTitle: { eq: "Form: Callback success message" }
      node_locale: { eq: $node_locale }
    ) {
      contentful_id
      alertInternalTitle
      alertHeader
      alertDescription {
        raw
      }
    }
    #End of success message for click to call submission

    #Success message for email forms submission
    successAlertEmail: contentfulAlert(
      alertInternalTitle: { eq: "Form: Email success message" }
      node_locale: { eq: $node_locale }
    ) {
      contentful_id
      alertInternalTitle
      alertHeader
      alertDescription {
        raw
      }
    }

    #End of success message for email forms submission
  }
`

export default ContactUs
